import React from 'react';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { usePageVisibility } from '@/hooks/usePageVisibility';

interface WaveBackgroundProps {
  variant: 'focus' | 'admin' | 'break';
  progress: number; // 0-100
}

export const WaveBackground = React.memo(function WaveBackground({ variant, progress }: WaveBackgroundProps) {
  const isVisible = usePageVisibility();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const colorVar = variant === 'break' ? 'success' : variant === 'focus' ? 'primary' : 'secondary';

  if (prefersReducedMotion || !isVisible) {
    return (
      <>
        {/* Static gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, hsl(var(--${colorVar}) / 0.08) 0%, hsl(var(--background)) 50%, hsl(var(--${colorVar}) / 0.05) 100%)`,
            opacity: 0.5,
          }}
        />
        {/* Static progress fill */}
        <div
          className="absolute bottom-0 left-0 w-full transition-all duration-1000 ease-out"
          style={{
            height: `${progress}%`,
            background: `linear-gradient(to top, hsl(var(--${colorVar}) / 0.1) 0%, transparent 100%)`,
          }}
        />
      </>
    );
  }

  return (
    <>
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0"
        animate={{
          opacity: [0.4, 0.6, 0.4],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          background: `linear-gradient(135deg, hsl(var(--${colorVar}) / 0.08) 0%, hsl(var(--background)) 50%, hsl(var(--${colorVar}) / 0.05) 100%)`,
        }}
      />

      {/* Progress fill from bottom - very subtle */}
      <div
        className="absolute bottom-0 left-0 w-full transition-all duration-1000 ease-out"
        style={{
          height: `${progress}%`,
          background: `linear-gradient(to top, hsl(var(--${colorVar}) / 0.1) 0%, transparent 100%)`,
        }}
      />

      {/* Slow-moving wave layers - 25-30s cycles to reduce GPU work */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute bottom-0 left-0 w-[200%] h-full opacity-[0.03]"
          style={{
            background: `linear-gradient(transparent, hsl(var(--foreground) / 0.1))`,
            willChange: 'transform',
          }}
          animate={{ x: [0, '-50%'] }}
          transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-[200%] h-full opacity-[0.02]"
          style={{
            background: `linear-gradient(transparent, hsl(var(--foreground) / 0.08))`,
            willChange: 'transform',
          }}
          animate={{ x: ['-50%', 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    </>
  );
});
